import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  setChatbotStatus,
  setChatbotTriggerType,
  setProactiveModalViewedPage,
  setGigaEventTriggeredPage,
} from '~/redux/actions/chatbot';
import { showChatbot, triggerAnimation } from './helpers/chatbotHelpers';
import { ChatTriggerType } from './useChatTriggers';
import { ModalKeys } from '~/constants/modal';
import { trackEvent } from '~/helpers/tracking';
import { ChatbotState } from '~/types/chatbot';
import { useModal } from '../useModal';
import { UseChatbotMonarchReturnType } from './useChatbotMonarch';

export interface UseChatCallbackParams {
  chatType: 'GIGA' | 'PROACTIVE_V1' | 'PROACTIVE_V2' | 'DISABLED';
  chatbotState: ChatbotState;
  currentPage: string;
  monarchFlags: UseChatbotMonarchReturnType;
}

export interface UseChatCallbackReturn {
  triggerChat: (type: ChatTriggerType) => void;
}

/**
 * Handles chat trigger logic for all chat types (PROACTIVE_V1, PROACTIVE_V2, GIGA)
 */
export const useChatCallback = ({
  chatType,
  chatbotState,
  currentPage,
  monarchFlags,
}: UseChatCallbackParams): UseChatCallbackReturn => {
  const dispatch = useDispatch();
  const { openModal } = useModal();

  /**
   * Proactive V1 implementation - shows chatbot widget
   */
  const triggerProactiveV1 = useCallback(
    (type: ChatTriggerType) => {
      if (chatbotState.status !== 'PROACTIVE_CHAT_VISIBLE') {
        trackEvent({
          action: 'elementViewed',
          data: {
            elementType: 'PROACTIVE_CHAT',
            position: 'chatbot_widget',
            location: 'proactive_chat_trigger',
            text: `Proactive chat triggered by ${type}`,
          },
        });

        dispatch(setChatbotStatus('PROACTIVE_CHAT_VISIBLE'));
        dispatch(setChatbotTriggerType(type));
      }
      showChatbot();
      triggerAnimation(monarchFlags.animate);
    },
    [chatbotState.status, dispatch, monarchFlags.animate]
  );

  /**
   * Proactive V2 implementation - opens modal
   */
  const triggerProactiveV2 = useCallback(
    (type: ChatTriggerType) => {
      // Check if modal was already viewed for this page
      if (!(chatbotState.proactiveModalViewed ?? {})[currentPage]) {
        trackEvent({
          action: 'elementViewed',
          data: {
            elementType: 'PROACTIVE_CHAT_V2',
            position: 'modal',
            location: 'proactive_modal',
            text: `Proactive chat triggered by ${type}`,
          },
        });
        openModal(ModalKeys.PROACTIVE_CHAT);
        dispatch(setProactiveModalViewedPage(currentPage, true));
        triggerAnimation(monarchFlags.animate);
      }
    },
    [currentPage, chatbotState.proactiveModalViewed, openModal, dispatch, monarchFlags.animate]
  );

  const triggerGigaChat = useCallback(
    (type: ChatTriggerType) => {
      if (!sessionStorage.getItem('gigaChatTriggered')) {
        sessionStorage.setItem('gigaChatTriggered', JSON.stringify({}));
      }

      // Using sessionStorage to avoid double processing as a workaround.
      // Based on the result of this implementation, we can add later a modal, and the modal execution will avoid double processing.
      const triggeredPages = JSON.parse(sessionStorage.getItem('gigaChatTriggered') || '{}');

      if (triggeredPages[currentPage]) {
        return;
      }

      triggeredPages[currentPage] = true;
      sessionStorage.setItem('gigaChatTriggered', JSON.stringify(triggeredPages));

      trackEvent({
        action: 'elementViewed',
        data: {
          elementType: 'GIGA_CHAT',
          position: 'WHAT_IF',
          location: 'WHAT_IF ' + currentPage,
          text: `Giga chat triggered by ${type}`,
        },
      });
      dispatch(setGigaEventTriggeredPage(currentPage, true));
      triggerAnimation(monarchFlags.animate);

      // Open modal if triggered by form-error (Monarch hook error)
      const shouldForceModal = type === 'form-error' && monarchFlags.triggers?.formError?.forceModal !== false;
      if (shouldForceModal) {
        openModal(ModalKeys.PROACTIVE_CHAT);
      }
    },
    [currentPage, chatbotState.gigaEventTriggered, dispatch, monarchFlags.animate]
  );

  /**
   * Main trigger function that delegates to appropriate implementation
   */
  const triggerChat = useCallback(
    (type: ChatTriggerType) => {
      if (chatType === 'PROACTIVE_V2') {
        triggerProactiveV2(type);
      } else if (chatType === 'GIGA') {
        triggerGigaChat(type);
      } else {
        triggerProactiveV1(type);
      }
    },
    [chatType, triggerGigaChat, triggerProactiveV1, triggerProactiveV2, monarchFlags.triggers]
  );

  return { triggerChat };
};
